import { API } from 'aws-amplify';
import { getUserLastLoginReport } from 'graphql/queries';

export interface DownloadReportOptions {
  orgid: string;
  format?: 'json' | 'csv';
}

/**
 * Downloads the user last login report for an organization
 * @param options - Download options including orgid and format
 * @returns Promise that resolves when download is complete
 */
export const downloadUserLastLoginReport = async (
  options: DownloadReportOptions
): Promise<void> => {
  const { orgid, format = 'csv' } = options;

  try {
    // Call the GraphQL query
    const response = await API.graphql({
      query: getUserLastLoginReport,
      variables: {
        request: JSON.stringify({
          orgid,
          format,
        }),
      },
    });

    // Parse the response
    const getUserLastLoginReportData = (response as any).data
      .getUserLastLoginReport;

    // Handle different response formats
    let result;
    if (typeof getUserLastLoginReportData === 'string') {
      result = JSON.parse(getUserLastLoginReportData);
    } else if (getUserLastLoginReportData.body) {
      result = JSON.parse(getUserLastLoginReportData.body);
    } else {
      result = getUserLastLoginReportData;
    }

    // Validate response
    if (!result || !result.data) {
      throw new Error('Invalid response format from API');
    }

    if (format === 'csv') {
      // Validate CSV data exists
      if (typeof result.data !== 'string' || result.data.length === 0) {
        throw new Error('No CSV data returned from API');
      }

      // Create a Blob from the CSV data with UTF-8 BOM for Excel compatibility
      const blob = new Blob(['\uFEFF' + result.data], {
        type: 'text/csv;charset=utf-8;',
      });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const timestamp = Date.now();
      const filename =
        result.filename || `user_last_login_report_${orgid}_${timestamp}.csv`;

      link.href = url;
      link.download = filename;
      link.style.display = 'none';

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup after a short delay to ensure download starts
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      console.log(
        `✅ CSV downloaded: ${filename} (${result.totalUsers} users)`
      );
      return Promise.resolve();
    } else {
      // JSON format - download as JSON file
      const jsonStr = JSON.stringify(result, null, 2);
      const blob = new Blob([jsonStr], { type: 'application/json' });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const timestamp = Date.now();
      link.href = url;
      link.download = `user_last_login_report_${orgid}_${timestamp}.json`;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      console.log(
        `✅ JSON downloaded: user_last_login_report_${orgid}_${timestamp}.json (${result.totalUsers} users)`
      );
      return Promise.resolve();
    }
  } catch (error) {
    console.error('❌ Error downloading user last login report:', error);

    // More detailed error message
    if ((error as any).errors) {
      console.error('GraphQL errors:', (error as any).errors);
    }

    throw error;
  }
};
